/**
 * Dev Mode Configuration Hook
 *
 * Provides runtime configuration management for local development,
 * including API target switching (local vs dev API) when using dev Cognito.
 *
 * Uses React Context + localStorage for reactive updates with persistence.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ApiTarget, DevModeConfig, DevModeContextValue } from '../config/types';

/** localStorage key for dev mode configuration */
const STORAGE_KEY = 'vibe-dev-mode-config';

/** Current schema version for migration support */
const CURRENT_VERSION = 1;

/** Default configuration when none exists */
const DEFAULT_CONFIG: DevModeConfig = {
  apiTarget: 'local',
  lastModified: new Date().toISOString(),
  version: 1,
};

/**
 * Get API URL for a specific target (lazy evaluation).
 *
 * Only validates the requested target's environment variable.
 * This avoids module-level crashes when variables for unused targets are missing.
 *
 * @param target - The API target to get URL for
 * @returns The API URL for the specified target
 * @throws Error if the required environment variable is not set
 */
function getApiUrlForTarget(target: ApiTarget): string {
  if (target === 'local') {
    return 'http://localhost:8000';
  }

  if (target === 'dev') {
    const devApiUrl = import.meta.env.VITE_DEV_API_URL;
    if (!devApiUrl) {
      throw new Error(
        'VITE_DEV_API_URL is required but not set. ' +
        'Please add it to your .env file. See README "Using as a Template" section.'
      );
    }
    return devApiUrl;
  }

  if (target === 'prod') {
    const prodApiUrl = import.meta.env.VITE_PROD_API_URL;
    if (!prodApiUrl) {
      throw new Error(
        'VITE_PROD_API_URL is required but not set. ' +
        'Please add it to your .env file. See README "Using as a Template" section.'
      );
    }
    return prodApiUrl;
  }

  // Fallback for any unexpected target
  return 'http://localhost:8000';
}

/**
 * Validate DevModeConfig structure
 * Returns true if config is valid, false otherwise
 */
export function validateDevModeConfig(config: unknown): config is DevModeConfig {
  if (typeof config !== 'object' || config === null) {
    return false;
  }

  const c = config as Record<string, unknown>;

  // Validate apiTarget
  if (!['local', 'dev', 'prod'].includes(c.apiTarget as string)) {
    return false;
  }

  // Validate lastModified is a valid ISO date string
  if (typeof c.lastModified !== 'string' || isNaN(Date.parse(c.lastModified))) {
    return false;
  }

  // Validate version
  if (c.version !== CURRENT_VERSION) {
    return false;
  }

  return true;
}

/**
 * Load dev mode configuration from localStorage with validation and fallback
 */
export function loadDevModeConfig(): DevModeConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_CONFIG;
    }

    const parsed = JSON.parse(stored);
    if (validateDevModeConfig(parsed)) {
      return parsed;
    }

    console.warn('[DevMode] Invalid config in localStorage, resetting to defaults');
    return DEFAULT_CONFIG;
  } catch (error) {
    console.warn('[DevMode] Failed to load config from localStorage:', error);
    return DEFAULT_CONFIG;
  }
}

/**
 * Save dev mode configuration to localStorage
 */
function saveDevModeConfig(config: DevModeConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('[DevMode] Failed to save config to localStorage:', error);
  }
}

/**
 * React Context for dev mode configuration
 */
const DevModeContext = createContext<DevModeContextValue | null>(null);

/**
 * Props for DevModeProvider
 */
interface DevModeProviderProps {
  children: React.ReactNode;
  /** Override for testing - determines if dev mode is available */
  forceDevModeAvailable?: boolean;
}

/**
 * Provider component for dev mode configuration
 *
 * Wraps the application to provide dev mode context throughout.
 * Determines availability based on environment (only available in local dev).
 */
export function DevModeProvider({
  children,
  forceDevModeAvailable,
}: DevModeProviderProps): JSX.Element {
  // Load initial config from localStorage
  const [config, setConfig] = useState<DevModeConfig>(() => loadDevModeConfig());

  // Determine if dev mode is available based on environment
  // Dev mode is available when:
  // 1. VITE_AUTHORITY is NOT set (not a deployed environment)
  // 2. VITE_ENVIRONMENT is 'dev' (using dev Cognito)
  const isDevModeAvailable = useMemo(() => {
    if (forceDevModeAvailable !== undefined) {
      return forceDevModeAvailable;
    }

    const isDeployed = !!import.meta.env.VITE_AUTHORITY;
    const envName = import.meta.env.VITE_ENVIRONMENT as string;

    // Only available in local development with dev environment
    return !isDeployed && envName === 'dev';
  }, [forceDevModeAvailable]);

  // Update API target
  const setApiTarget = useCallback((target: ApiTarget) => {
    const newConfig: DevModeConfig = {
      ...config,
      apiTarget: target,
      lastModified: new Date().toISOString(),
    };
    setConfig(newConfig);
    saveDevModeConfig(newConfig);
    console.log(`[DevMode] API target changed to: ${target}`);
  }, [config]);

  // Reset to default configuration
  const resetConfig = useCallback(() => {
    const newConfig = {
      ...DEFAULT_CONFIG,
      lastModified: new Date().toISOString(),
    };
    setConfig(newConfig);
    saveDevModeConfig(newConfig);
    console.log('[DevMode] Config reset to defaults');
  }, []);

  // Get effective API URL based on current configuration and environment
  const getEffectiveApiUrl = useCallback((): string => {
    // If dev mode is not available, use environment-based URL
    if (!isDevModeAvailable) {
      // Check for deployed environment first (Terraform-injected VITE_API_URL)
      if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
      }

      // Fall back to environment-specific URLs (lazy evaluation)
      const envName = import.meta.env.VITE_ENVIRONMENT as string;
      if (envName === 'prod') {
        return getApiUrlForTarget('prod');
      }
      if (envName === 'dev') {
        return getApiUrlForTarget('dev');
      }
      return getApiUrlForTarget('local');
    }

    // Dev mode is available - use the selected API target (lazy evaluation)
    return getApiUrlForTarget(config.apiTarget);
  }, [isDevModeAvailable, config.apiTarget]);

  const contextValue: DevModeContextValue = useMemo(
    () => ({
      config,
      isDevModeAvailable,
      setApiTarget,
      resetConfig,
      getEffectiveApiUrl,
    }),
    [config, isDevModeAvailable, setApiTarget, resetConfig, getEffectiveApiUrl]
  );

  return (
    <DevModeContext.Provider value={contextValue}>
      {children}
    </DevModeContext.Provider>
  );
}

/**
 * Hook to access dev mode configuration
 *
 * Must be used within a DevModeProvider.
 *
 * @returns DevModeContextValue with config and update methods
 * @throws Error if used outside of DevModeProvider
 */
export function useDevModeConfig(): DevModeContextValue {
  const context = useContext(DevModeContext);

  if (context === null) {
    throw new Error('useDevModeConfig must be used within a DevModeProvider');
  }

  return context;
}

/**
 * Export the context for testing purposes
 */
export { DevModeContext };
